// src/components/tutorials/JS_Random.jsx
import React, { useRef } from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";
import TryItButton from "../../../TryItButton";
import OutputBox from "../../../OutputBox";

export default function JS_Random() {
  const refs = {
    BASIC: useRef(null),
    RANGE: useRef(null),
    INTEGER: useRef(null),
    ARRAY_PICK: useRef(null),
    COLOR: useRef(null),
    PASSWORD: useRef(null),
  };

  const scrollTo = (key) =>
    refs[key]?.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🎲 JavaScript Math.random()</h1>
      <p className="tutorial-intro">
        <b>Math.random()</b> returns a pseudo-random number between{" "}
        <code className="highlight_code">0 (inclusive)</code> and{" "}
        <code className="highlight_code">1 (exclusive)</code>. You can use it to
        generate random integers, colors, passwords, or pick random items from
        arrays.
      </p>

      {/* Two-column Key Section */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "2rem",
          background: "linear-gradient(135deg, #cffafe, #a5f3fc)",
          padding: "1.5rem",
          borderRadius: "12px",
          marginTop: "1rem",
        }}
      >
        {/* Column 1 */}
        <div>
          <h2 style={{ margin: 0, color: "#0e7490" }}>Random Examples</h2>
          <ul
            style={{ listStyleType: "none", padding: 0, marginTop: "0.5rem" }}
          >
            {[
              { key: "BASIC", label: "Basic Random Number" },
              { key: "RANGE", label: "Random Number (Range)" },
              { key: "INTEGER", label: "Random Integer" },
            ].map((item) => (
              <li key={item.key}>
                <button
                  onClick={() => scrollTo(item.key)}
                  style={{
                    background: "transparent",
                    border: "none",
                    textDecoration: "underline",
                    color: "black",
                    cursor: "pointer",
                    fontSize: "1.05rem",
                    marginBottom: "0.3rem",
                  }}
                >
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Column 2 */}
        <div>
          <h2 style={{ margin: 0, color: "#0e7490" }}>&nbsp;</h2>
          <ul
            style={{ listStyleType: "none", padding: 0, marginTop: "0.5rem" }}
          >
            {[
              { key: "ARRAY_PICK", label: "Pick Random Array Item" },
              { key: "COLOR", label: "Random Color Generator" },
              { key: "PASSWORD", label: "Random Password Generator" },
            ].map((item) => (
              <li key={item.key}>
                <button
                  onClick={() => scrollTo(item.key)}
                  style={{
                    background: "transparent",
                    border: "none",
                    textDecoration: "underline",
                    color: "black",
                    cursor: "pointer",
                    fontSize: "1.05rem",
                    marginBottom: "0.3rem",
                  }}
                >
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Detailed Sections */}

      {/* BASIC */}
      <div ref={refs.BASIC} className="tutorial-section">
        <h3>1️⃣ Basic Random Number</h3>
        <p>
          Generates a random decimal number between 0 (inclusive) and 1
          (exclusive).
        </p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock code={`console.log(Math.random());`} />
          <TryItButton code={`console.log(Math.random());`} />
        </div>
        <OutputBox>Output: 0.3456721234 (varies each time)</OutputBox>
      </div>

      {/* RANGE */}
      <div ref={refs.RANGE} className="tutorial-section">
        <h3>2️⃣ Random Number in a Range</h3>
        <p>
          Generate a random number between{" "}
          <code className="highlight_code">min</code> and{" "}
          <code className="highlight_code">max</code>.
        </p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`function getRandom(min, max) {
  return Math.random() * (max - min) + min;
}
console.log(getRandom(5, 15));`}
          />
          <TryItButton
            code={`function getRandom(min, max) {
  return Math.random() * (max - min) + min;
}
console.log(getRandom(5, 15));`}
          />
        </div>
        <OutputBox>Output: Between 5 and 15 (varies each run)</OutputBox>
      </div>

      {/* INTEGER */}
      <div ref={refs.INTEGER} className="tutorial-section">
        <h3>3️⃣ Random Integer</h3>
        <p>
          Generate a random integer between{" "}
          <code className="highlight_code">min</code> and{" "}
          <code className="highlight_code">max</code> inclusive.
        </p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`function getRandomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}
console.log(getRandomInt(1, 6)); // like a dice roll`}
          />
          <TryItButton
            code={`function getRandomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}
console.log(getRandomInt(1, 6));`}
          />
        </div>
        <OutputBox>Output: Random integer between 1–6</OutputBox>
      </div>

      {/* ARRAY_PICK */}
      <div ref={refs.ARRAY_PICK} className="tutorial-section">
        <h3>4️⃣ Pick Random Array Item</h3>
        <p>Randomly selects an element from an array.</p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`const fruits = ["Apple", "Banana", "Cherry", "Mango"];
const randomFruit = fruits[Math.floor(Math.random() * fruits.length)];
console.log(randomFruit);`}
          />
          <TryItButton
            code={`const fruits = ["Apple", "Banana", "Cherry", "Mango"];
const randomFruit = fruits[Math.floor(Math.random() * fruits.length)];
console.log(randomFruit);`}
          />
        </div>
        <OutputBox>Output: "Banana" (random)</OutputBox>
      </div>

      {/* COLOR */}
      <div ref={refs.COLOR} className="tutorial-section">
        <h3>5️⃣ Random Color Generator</h3>
        <p>Create a random color using hexadecimal values.</p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`function randomColor() {
  return "#" + Math.floor(Math.random() * 16777215).toString(16);
}
console.log(randomColor());`}
          />
          <TryItButton
            code={`function randomColor() {
  return "#" + Math.floor(Math.random() * 16777215).toString(16);
}
console.log(randomColor());`}
          />
        </div>
        <OutputBox>Output: #a3f9d2 (random)</OutputBox>
      </div>

      {/* PASSWORD */}
      <div ref={refs.PASSWORD} className="tutorial-section">
        <h3>6️⃣ Random Password Generator</h3>
        <p>Generates a random alphanumeric password.</p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`function generatePassword(length) {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let password = "";
  for (let i = 0; i < length; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return password;
}
console.log(generatePassword(8));`}
          />
          <TryItButton
            code={`function generatePassword(length) {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let password = "";
  for (let i = 0; i < length; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return password;
}
console.log(generatePassword(8));`}
          />
        </div>
        <OutputBox>Output: Random 8-character password</OutputBox>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #cffafe, #a5f3fc)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#0e7490" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Use <code className="highlight_code">Math.random()</code> with{" "}
            <code className="highlight_code">Math.floor()</code> for integer
            generation.
          </li>
          <li>
            Build fun mini projects — dice roller, password generator, color
            picker.
          </li>
          <li>Combine randomness with arrays and loops for creative logic.</li>
        </ul>
      </div>
    </div>
  );
}
